import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import NewsCard from "@/components/feature/NewsCard";
import { categories } from "@/types/content";
import { getPopular, searchNews } from "@/lib/content";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") ?? "";
  const [input, setInput] = useState(initialQ);
  const [keyword, setKeyword] = useState(initialQ);
  const [activeCats, setActiveCats] = useState<string[]>([]);

  useEffect(() => {
    const q = params.get("q") ?? "";
    setInput(q);
    setKeyword(q);
  }, [params]);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 小蜜桃` : "全站搜索 - 小蜜桃",
    description: "在小蜜桃搜索影视、音乐、生活方式与品牌相关娱乐资讯，快速找到你感兴趣的内容。",
    keywords: "小蜜桃搜索,娱乐资讯搜索,影视,音乐,生活方式,品牌",
  });

  const results = useMemo(() => searchNews(keyword, activeCats), [keyword, activeCats]);
  const recommend = useMemo(() => getPopular(3), []);

  const toggleCat = (key: string) => {
    setActiveCats((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setKeyword(q);
    if (q) {
      setParams({ q });
    } else {
      setParams({});
    }
  };

  const reset = () => {
    setInput("");
    setKeyword("");
    setActiveCats([]);
    setParams({});
  };

  const highlight = (text: string) => {
    if (!keyword.trim()) return text;
    const idx = text.toLowerCase().indexOf(keyword.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-primary-100 text-primary-800 rounded px-0.5">
          {text.slice(idx, idx + keyword.length)}
        </mark>
        {text.slice(idx + keyword.length)}
      </>
    );
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-8 md:pt-12">
        <div className="max-w-3xl">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
            全站搜索
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            输入关键词，在影视、音乐、生活方式与品牌内容中查找
          </p>

          <form
            onSubmit={submit}
            className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-background-50 border border-background-200 rounded-lg sm:rounded-md p-2 sm:p-1.5 focus-within:border-primary-400 transition-colors"
          >
            <span className="w-6 h-6 ml-1 hidden sm:flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="text"
              placeholder="例如：科幻电影、民谣、早餐、品牌合作"
              className="flex-1 bg-transparent outline-none text-sm px-3 py-2.5 placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-6 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-6 md:py-8 flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500 mr-1">分类范围</span>
          {categories.map((cat) => (
            <button
              key={cat.key}
              type="button"
              onClick={() => toggleCat(cat.key)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                activeCats.includes(cat.key)
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className={cat.icon}></i>
              {cat.name}
            </button>
          ))}
          {activeCats.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveCats([])}
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer ml-1"
            >
              清除分类
            </button>
          )}
        </div>

        <div className="flex items-center justify-between border-b border-background-200 pb-3">
          <p className="text-sm text-foreground-600">
            {keyword ? (
              <>
                关键词 “<span className="text-primary-600 font-medium">{keyword}</span>” 共找到{" "}
                <span className="text-primary-600 font-medium">{results.length}</span> 条结果
              </>
            ) : (
              <>
                为你展示全部 <span className="text-primary-600 font-medium">{results.length}</span>{" "}
                条内容
              </>
            )}
          </p>
          {(keyword || activeCats.length > 0) && (
            <button
              type="button"
              onClick={reset}
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置搜索
            </button>
          )}
        </div>

        {results.length === 0 ? (
          <div className="flex flex-col items-center text-center py-10">
            <span className="w-14 h-14 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <p className="mt-4 font-heading font-semibold text-lg text-foreground-950">
              没有找到相关内容
            </p>
            <p className="mt-1 text-sm text-foreground-500">
              换个关键词试试，或者看看下面的热门推荐
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 border border-background-200 text-foreground-700 text-sm px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置搜索
            </button>

            <div className="mt-10 w-full text-left">
              <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">
                热门推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {recommend.map((item, idx) => (
                  <NewsCard key={item.id} item={item} index={idx} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-5">
            {results.map((item, idx) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                style={{ animationDelay: `${Math.min(idx * 50, 300)}ms` }}
                className="group animate-fade-up flex flex-col sm:flex-row gap-4 bg-background-50 border border-background-200 rounded-lg p-4 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-full sm:w-44 h-40 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} - 小蜜桃搜索结果`}
                    title={`${item.title} - 小蜜桃`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span className="text-primary-600">{item.categoryName}</span>
                    <span>·</span>
                    <span>{item.subType}</span>
                  </div>
                  <h2 className="mt-2 font-heading font-semibold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {highlight(item.title)}
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600 line-clamp-2">
                    {highlight(item.summary)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}